import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import './GlobalLogin.css';
import { AuthSetupShell, IconLock, IconEye, IconArrow, IconKey, IconCheck } from './authShared';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!token) {
      setError('Invalid or missing reset link');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      const data = await res.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(data.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthSetupShell>
        <div className="gl-auth__badge">
          <IconKey />
        </div>
        <header className="gl-auth__card-head">
          <h2>Password reset</h2>
          <p>Invalid link</p>
        </header>
        <p className="gl-auth__hint">
          This reset link is invalid or has expired. Please request a new one.
        </p>
        <button className="gl-auth__submit" onClick={() => navigate('/login')}>
          <span>Go to login</span>
          <IconArrow />
        </button>
      </AuthSetupShell>
    );
  }

  if (success) {
    return (
      <AuthSetupShell>
        <div className="gl-auth__badge">
          <IconCheck />
        </div>
        <header className="gl-auth__card-head">
          <h2>Password reset</h2>
          <p>You're all set</p>
        </header>
        <p className="gl-auth__hint">
          Your new password is saved. Redirecting you to login…
        </p>
      </AuthSetupShell>
    );
  }

  return (
    <AuthSetupShell>
      <div className="gl-auth__badge">
        <IconKey />
      </div>
      <header className="gl-auth__card-head">
        <h2>Password reset</h2>
        <p>Choose a new password</p>
      </header>
      <p className="gl-auth__hint">
        Enter a new password for your staff account.
      </p>
      <form className="gl-auth__form" onSubmit={handleSubmit}>
        <div className="gl-auth__field">
          <label className="gl-auth__label" htmlFor="rp-password">
            New password
          </label>
          <div className="gl-auth__input-wrap">
            <span className="gl-auth__input-icon" aria-hidden>
              <IconLock />
            </span>
            <input
              type={showPassword ? 'text' : 'password'}
              id="rp-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 6 characters"
              className="gl-auth__input gl-auth__input--password"
              required
              minLength={6}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="gl-auth__toggle-pw"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <IconEye off={showPassword} />
            </button>
          </div>
        </div>

        <div className="gl-auth__field">
          <label className="gl-auth__label" htmlFor="rp-confirm">
            Confirm password
          </label>
          <div className="gl-auth__input-wrap">
            <span className="gl-auth__input-icon" aria-hidden>
              <IconLock />
            </span>
            <input
              type={showConfirm ? 'text' : 'password'}
              id="rp-confirm"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              className="gl-auth__input gl-auth__input--password"
              required
              minLength={6}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="gl-auth__toggle-pw"
              onClick={() => setShowConfirm(!showConfirm)}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              <IconEye off={showConfirm} />
            </button>
          </div>
        </div>

        {error ? <div className="gl-auth__error">{error}</div> : null}

        <button type="submit" className="gl-auth__submit" disabled={loading}>
          <span>{loading ? 'Resetting…' : 'Reset password'}</span>
          {!loading ? <IconArrow /> : null}
        </button>

        <div className="customer-access-link">
          <Link to="/login">Back to login</Link>
        </div>
      </form>
    </AuthSetupShell>
  );
};

export default ResetPassword;
